import {
    Form,
    FormLayout,
    TextField,
    Button,
    ResourceItem,
    ResourceList,
    LegacyCard,
    Layout,
    Toast,
    Badge,
    Text,
} from "@shopify/polaris";
import {useState, useCallback, useEffect, useMemo} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import Api from '../../helpers/api';
import { normalize_phone_number } from '../../helpers/utils';
import {EmptyStateWithSpinner} from "../Helpers";

function findMatchingLocation(query, customerData) {
    const normalizedPhoneNumber = normalize_phone_number(query);

    return normalizedPhoneNumber
        ? customerData.locations.find(item => item.normalized_phone_number === normalizedPhoneNumber)
        : null;
}

function useQuery() {
    const { search } = useLocation();

    return useMemo(() => new URLSearchParams(search), [search]);
}

const SearchCustomer = props => {
    const history = useHistory();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [errors, setErrors] = useState(null);
    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState({
        active: false,
        message: ''
    });

    const queryString = useQuery();

    const handleSubmit = () => {
        if (query == '') {
            setErrors('Please type something in the search field.')
            return;
        };

        history.push('/customers?' + (new URLSearchParams({q: query})));
        loadCustomers(query, true);
    }

    const loadCustomers = (q, canRedirect) => {
        setErrors(null);
        setLoading(true);
        Api.findCustomer({
            query: q
        })
            .then(({data: result}) => {
                if (result.data.length === 1 && canRedirect) {
                    const customerData = result.data[0];

                    if (q.startsWith('#')) {
                        assignCustomer(customerData, q);
                    } else {
                        assignCustomer(customerData);
                    }
                } else {
                    setResults(result.data);
                }
            })
            .catch(() => setToast({active: true, message: "Server error, please try again."}))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (queryString.get('q') == '' || queryString.get('q') == null) {
            return;
        }

        setQuery(queryString.get('q'));
        loadCustomers(queryString.get('q'), false);
    }, []);

    const assignCustomer = (customer, query) => {
        props.onCustomerSelect(customer, query);
        setResults([]);
        setQuery('');
    }

    const handleQueryChange = useCallback(
        (value) => setQuery(value),
        [],
    );

    const toastMarkup = toast.active ? (
        <Toast content={toast.message} onDismiss={() => {setToast({message: '', active: false})}} />
      ) : null;

    return (
        <Layout>
        <Layout.Section>
            <Form onSubmit={handleSubmit}>
                <FormLayout>
                    <TextField
                        autoComplete={''}
                        value={query}
                        onChange={handleQueryChange}
                        label="Search by Phone or Customer Account Number"
                        type="text"
                        error={errors}
                        connectedRight={<Button loading={loading} submit  variant="primary">Search</Button>}
                    />
                </FormLayout>
            </Form>
        </Layout.Section>

        <Layout.Section>
            <LegacyCard title="Search Results">
                <ResourceList
                    resourceName={{ singular: 'customer', plural: 'customers' }}
                    loading={results.length > 0 && loading}
                    emptyState={<EmptyStateWithSpinner
                        heading={"Manage your customers"}
                        action={props.emptyStateAction}
                        loading={loading}
                    />}
                    items={results}
                    renderItem={(item) => {
                        let customerName = '';

                        if (item.account_number) {
                            customerName += `#${item.account_number} - `;
                        }

                        customerName += `${item.first_name} ${item.last_name}`;

                        return (
                            <ResourceItem
                                id={item.id}
                                onClick={event => {
                                    assignCustomer(item)
                                }}
                                accessibilityLabel={`View details for account ${item.account_number}`}
                            >
                                <h3>
                                    <Text variant="bodyMd" as="span" fontWeight="semibold">{customerName}</Text>
                                </h3>
                                <div>{`Company: ${item.company_name}`}</div>
                                {item.shopify_users && item.shopify_users.length > 0 && (
                                    item.shopify_users.map((shopifyUser) => <Badge
                                        tone={"info"}>{shopifyUser.name}</Badge>)
                                )}
                                {(!item.shopify_users || item.shopify_users.length == 0) && (
                                    <Badge tone={"warning"}>No Sales Rep</Badge>
                                )}
                                <div>{`Phone: ${item.additional_data.home_phone} - Email: ${item.email}`}</div>
                            </ResourceItem>
                        );
                    }}
                />
            </LegacyCard>
        </Layout.Section>
        {toastMarkup}
    </Layout>
    );
}

export default SearchCustomer;
